import { RequiredParamException } from "api/exceptions/InvalidParamException";
import ContractRowMovementModel from "api/models/ContractRowMovementModel";
import { getDoc, getDocs, query, Transaction, where } from "firebase/firestore";
import {
  fromDocumentData,
  getCollectionRef,
  getDocumentRef,
  setDocument,
  toDocumentData,
} from "./FirebaseRepository";

class ContractRowMovementRepository {
  private path = (contractUid: string) => `cfContract/${contractUid}/movement`;

  public async getMovementRowFromDataBaseByAttachmentUid(
    contractUid: string,
    attachmentUid: string
  ) {
    const collectionPath = this.path(contractUid);
    const collection = getCollectionRef(collectionPath);

    const queryRef = query(
      collection,
      where("allAttachments", "array-contains", attachmentUid)
    );
    const snapshot = await getDocs(queryRef);
    return snapshot.docs;
  }

  public async getMovementRowFromDataBase(
    contractUid: string,
    rowUid: string
  ): Promise<ContractRowMovementModel | undefined> {
    if (!contractUid) throw new RequiredParamException("contractUid");
    if (!rowUid) throw new RequiredParamException("uid");

    const doc = getDocumentRef(
      this.path(contractUid),
      rowUid,
      converter(contractUid)
    );
    return getDoc(doc).then((snapshot) =>
      converter(contractUid).fromFirestore(snapshot, {})
    );
  }

  public async getAllMovementRowsFromDataBase(
    contractUid: string
  ): Promise<ContractRowMovementModel[]> {
    const collectionPath = this.path(contractUid);
    const collection = getCollectionRef(collectionPath);

    const snapshot = await getDocs(collection);
    return snapshot.docs.map((doc) =>
      converter(contractUid).fromFirestore(doc, {})
    );
  }

  public async setMovementRowOfDataBase(
    data: ContractRowMovementModel,
    transaction?: Transaction
  ): Promise<void> {
    if (!data.contractUid) throw new RequiredParamException("contractUid");
    if (!data.uid) throw new RequiredParamException("uid");

    const doc = getDocumentRef(
      this.path(data.contractUid),
      data.uid,
      converter(data.contractUid)
    );
    return setDocument(doc, data, transaction);
  }
}

// Firestore data converter
const converter = (contractUid) => ({
  toFirestore: (movement: any) => {
    const data =
      movement instanceof ContractRowMovementModel
        ? movement.toSave()
        : movement;
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);
    return ContractRowMovementModel.create(snapshot.id, {
      ...data,
      contractUid,
    });
  },
});

export default new ContractRowMovementRepository();
